import {
  IconEllipsisV,
  IconPlus,
  IconPlusCircle,
  IconShieldCheck,
} from '@maia/icons';
import {
  ButtonMenu,
  CardList,
  PageHeader,
  showToast,
  Subnav,
  Tag,
  TextButton,
} from '@maia/ui';
import { createFileRoute, linkOptions, Outlet } from '@tanstack/react-router';

import { service } from '@maia/cart';
import { IAttachmentEntity } from '@maia/shared';
import { fileToBase64, RefProvider } from '@maia/utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { getInsuranceOptions } from '../../../../features/insurance/insurance/queryOptions';
import { getNotesOptions } from '../../../../features/insurance/notes/queryOptions';
import { CustomerInformation } from '../../../../features/shared/customer/customer-information';
import { RecentNoteCard } from '../../../../features/shared/note/recent-note-card';
import { useCreateInsuranceNote } from './../../../../features/insurance/notes/create-note.options';
import { AddNoteSidedrawer } from './../../../../features/shared/note/add-note-sidedrawer';
import { NoteSidedrawer } from './../../../../features/shared/note/note-sidedrawer';
import { queryClient } from './../../../../main';
import { useDrawerStore } from './../../../../stores/drawer.store';
import { ActivitySidedrawer } from './../../../../features/shared/activity/activity-sidedrawer';
import { TaskSidedrawer } from './../../../../features/shared/task/task-sidedrawer';

export const Route = createFileRoute(
  '/_authenticated/insurances/$guid/_layout',
)({
  component: Layout,
  loader: async ({ context: { queryClient }, params: { guid } }) => {
    const insurance = await queryClient.ensureQueryData(
      getInsuranceOptions(guid),
    );

    return { insurance };
  },
});

function Layout() {
  const { guid } = Route.useParams();
  const data = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const { openDrawer, closeDrawer } = useDrawerStore();

  const { data: notes } = useQuery(
    getNotesOptions(guid, {
      page: 1,
      limit: 3,
      sort: 'created_at,desc',
    }),
  );

  const { mutateAsync: createNote } = useCreateInsuranceNote(
    data.insurance.customer.guid,
    data.insurance.guid,
    () => {
      queryClient.invalidateQueries({
        queryKey: ['notes'],
        exact: false,
      });
      showToast({
        intent: 'success',
        title: 'Notitie toegevoegd',
        config: { position: 'top-right' },
      });
      closeDrawer();
    },
    () => {
      showToast({
        intent: 'error',
        title: 'Er ging iets mis',
        description:
          'We konden niet de notitie voor je aanmaken. Probeer het later opnieuw',
        config: { position: 'top-right' },
      });
    },
  );

  const { mutate: createMutation, isPending: isPendingMutation } = useMutation({
    mutationFn: () =>
      service.createMutation({
        customerGuid: data.insurance.customer.guid,
        insuranceGuid: data.insurance.guid,
      }),
    onSuccess: ({ cart_guid }) =>
      navigate({
        to: '/edit-insurance/$customer/cart/$cart',
        params: { customer: data.insurance.customer.guid, cart: cart_guid },
      }),
    onError: () => {
      showToast({
        intent: 'error',
        title: 'Er ging iets mis',
        description:
          'We konden de mutatie niet starten. Probeer het later opnieuw',
        config: { position: 'top-right' },
      });
    },
  });

  const { mutate: createCancellation, isPending: isPendingCancellation } =
    useMutation({
      mutationFn: () => {
        return service.createCancellation({
          customerGuid: data.insurance.customer.guid,
          insuranceGuid: data.insurance.guid,
        });
      },
      onSuccess: ({ cancellation_guid }) =>
        navigate({
          to: '/cancel-insurance/$customer/cart/$cart/insurance/$insurance',
          params: {
            customer: data.insurance.customer.guid,
            cart: cancellation_guid,
            insurance: data.insurance.guid,
          },
        }),
      onError: () => {
        showToast({
          intent: 'error',
          title: 'Er ging iets mis',
          description:
            'We konden de opzegging niet starten. Probeer het later opnieuw',
          config: { position: 'top-right' },
        });
      },
    });

  return (
    <div className="layout-sidebar">
      <PageHeader
        icon={IconShieldCheck}
        title={data.insurance.title}
        reference={`#${data.insurance.reference ?? 'Nog onbekend'}`}
        isSpecialLimit={data.insurance.isSpecialLimit}
        actions={
          <div className="flex gap-x-32">
            {(data.insurance.isMutable || data.insurance.is_cancellable) && (
              <ButtonMenu
                intent="tertiary"
                size="default"
                iconRight={IconEllipsisV}
                isLoading={isPendingMutation || isPendingCancellation}
                isDisabled={isPendingMutation || isPendingCancellation}
                menuItems={[
                  ...(data.insurance.isMutable
                    ? [
                        {
                          icon: 'pen',
                          text: 'Verzekering wijzigen',
                          onAction: createMutation,
                        },
                      ]
                    : []),

                  ...(data.insurance.is_cancellable
                    ? [
                        {
                          icon: 'trash-alt',
                          text: 'Verzekering opzeggen',
                          intent: 'destructive',
                          onAction: createCancellation,
                        } as const,
                      ]
                    : []),
                ]}
              >
                Bewerk verzekering
              </ButtonMenu>
            )}
            <ButtonMenu
              intent="cta"
              size="default"
              iconLeft={IconPlus}
              menuItems={[
                {
                  icon: 'fire',
                  text: 'Nieuwe claim',
                  href: `/claims/new/${data.insurance.customer.guid}?insurance=${data.insurance.guid}`,
                },
                {
                  icon: 'check-square',
                  text: 'Nieuwe taak',
                  href: `/tasks/new/insurance?guid=${data.insurance.guid}`,
                },
                {
                  icon: 'notes',
                  text: 'Nieuwe notitie',
                  onAction: () => openDrawer({ type: 'note.add' }),
                },
              ]}
            >
              Toevoegen
            </ButtonMenu>
          </div>
        }
        status={
          <Tag
            {...data.insurance.status}
            tooltip={data.insurance.acceptanceRemark ?? undefined}
          />
        }
        className="area-page-header"
      />

      <Subnav
        className="area-page-subnav"
        links={[
          linkOptions({
            to: '/insurances/$guid',
            params: {
              guid,
            },
            label: 'Overzicht',
          }),
          linkOptions({
            to: '/insurances/$guid/documents',
            params: {
              guid,
            },
            label: 'Documenten',
          }),
          linkOptions({
            to: '/insurances/$guid/activities',
            params: {
              guid,
            },
            label: 'Activiteit',
          }),
          linkOptions({
            to: '/insurances/$guid/claims',
            params: {
              guid,
            },
            label: 'Claims',
          }),
          linkOptions({
            to: '/insurances/$guid/tasks',
            params: {
              guid,
            },
            label: 'Taken',
          }),
          linkOptions({
            to: '/insurances/$guid/notes',
            params: {
              guid,
            },
            label: 'Notities',
          }),
        ]}
      />

      <div className="area-page-content content-grid">
        <Outlet />
      </div>

      <RefProvider<HTMLDivElement>>
        {({ ref, trackedElement }) => (
          <div className="area-page-sidebar flex flex-col gap-y-32" ref={ref}>
            <Suspense fallback={null}>
              <CustomerInformation customer={data.insurance.customer} />
            </Suspense>

            <CardList
              title="Recente notities"
              items={notes?.response ?? []}
              render={(note) => (
                <RecentNoteCard
                  note={note}
                  onClick={() => openDrawer({ type: 'note', data: note })}
                />
              )}
              resource="recente notities"
              action={
                <TextButton
                  size="tiny"
                  iconLeft={IconPlusCircle}
                  onPress={() => openDrawer({ type: 'note.add' })}
                >
                  Voeg notitie toe
                </TextButton>
              }
            />
            <TaskSidedrawer portalRef={trackedElement} />
            <ActivitySidedrawer portalRef={trackedElement} />
            <NoteSidedrawer portalRef={trackedElement} />

            <AddNoteSidedrawer
              portalRef={trackedElement}
              onSubmit={async (values) => {
                const files: Array<IAttachmentEntity> = await Promise.all(
                  values.explanation.files.map(async (file) => {
                    const base64 = await fileToBase64(file);

                    return {
                      filename: file.name,
                      mimetype: file.type,
                      encoded_document: base64,
                    };
                  }),
                );

                await createNote({
                  category: values.type,
                  title: values.subject,
                  description: values.explanation.markdown,
                  attachments: files,
                });
              }}
            />
          </div>
        )}
      </RefProvider>
    </div>
  );
}
